import React from 'react';
import {useIntl} from 'react-intl';
import {
	Accordion,
	FlexColumn,
	FlexRow,
	FlexItem,
	Label,
	Text,
	TextField,
	DateRangePicker,
	IconTooltip,
	Checkbox,
	Toggle,
	DropdownList,
	ButtonDropdownField,
} from '@forecast-it/design-system';
import {LabelDropdown} from '../../../components/dropdowns/LabelDropdown/LabelDropdown';
import type {LabelDropdown_labels$data} from '../../../components/dropdowns/LabelDropdown/__generated__/LabelDropdown_labels.graphql';
import ResolutionCallout from './ResolutionCallout';
import type {ProjectProposal, DuplicateableProject, DuplicateScopeOptions} from './types';
// Same 6-colour subset CreateOpportunityModal uses (projectColors.slice(0, 6)) — sourced from
// web-front/src/constants.ts's `projectColors` export, not redefined here to avoid drift.
import {projectColors, MODULE_TYPES} from '../../../constants';
import {hasModule} from '../../../forecast-app/shared/util/ModuleUtil';
import CompanySetupUtil from '../../../forecast-app/shared/util/CompanySetupUtil';

// The label option shape LabelDropdown consumes (id/name/color/category), sourced from
// AiProjectPlanningModal's apiKeyQuery `company.labels` field — added alongside this task the
// same way Task 1 added `rateCards`/`defaultRateCard` (a new field under the existing
// @include(if: $includeSowData) block), mirroring CreateOpportunityModal_Query's own `labels`
// selection verbatim (id/name/color/category{id,name}).
//
// Not on ProjectProposal: Dylan's ticket ruling (2026-09-16) names Salesperson, Win Probability
// and Project ID as real-but-unsubmitted fields (spec decision D8) — Contact, Rate Card, Use
// Baseline and Labels were initially treated the same way, but direct comparison against
// new_project_modal_v2.js's own mutation-construction code showed the old modal always sent all
// four, so they're lifted to AiProjectPlanningModal and submitted (see selectedLabelIds' own
// comment above) rather than left as local-only state. ProjectProposal itself still has no
// `labels` field — the selection is threaded through AiProjectPlanningModal's own state, not
// through onProposalChange/resumeAgent.
type LabelOption = {id: string; name: string; color: string; category: {id: string; name: string} | null};

export interface DetailsSectionProps {
	proposal: ProjectProposal;
	labels: LabelOption[];
	datesCallout?: {question: string};
	onProposalChange: (proposal: ProjectProposal) => void;
	onFieldUserEdited: (field: string) => void;
	onCalloutResolve: (answer: string) => void;
	// Duplicate Project (TNSR-635 review — Animesh's manual QA against Figma directly: a Toggle
	// below Labels, styled like Use Baseline/Create Invoices if Available, not a Checkbox replacing
	// Labels — an earlier pass got both the component and the placement wrong). Lifted to the
	// caller (like canCreateInvoices is for FinancialSetupSection) since it must survive to
	// handleApprove's submit branch.
	duplicateableProjects: DuplicateableProject[];
	duplicateProject: boolean;
	onDuplicateProjectToggle: (checked: boolean) => void;
	projectToDuplicate: DuplicateableProject | null;
	onProjectToDuplicateChange: (project: DuplicateableProject | null) => void;
	// The granular clone-scope checkboxes (Structure/Resourcing/Financials/Setup) Figma shows once
	// a source project is picked — same lifted-state reasoning as duplicateProject/projectToDuplicate.
	duplicateScope: DuplicateScopeOptions;
	onDuplicateScopeChange: (scope: DuplicateScopeOptions) => void;
	// A SoW upload and a duplicate are mutually exclusive ways to create a project — true once a
	// document is attached, so the toggle disables rather than silently doing nothing if checked.
	duplicateProjectDisabled: boolean;
	// Labels: the old NEW_PROJECT_V2 modal this ticket replaced always sent selected labels (both
	// create and duplicate) — lifted to the caller so AiProjectPlanningModal can submit them,
	// matching the Rate Card/Use Baseline reframing in FinancialSetupSection.tsx.
	selectedLabelIds: string[];
	onSelectedLabelIdsChange: (labelIds: string[]) => void;
}

const DetailsSection = ({
	proposal,
	labels,
	datesCallout,
	onProposalChange,
	onFieldUserEdited,
	onCalloutResolve,
	duplicateableProjects,
	duplicateProject,
	onDuplicateProjectToggle,
	projectToDuplicate,
	onProjectToDuplicateChange,
	duplicateScope,
	onDuplicateScopeChange,
	duplicateProjectDisabled,
	selectedLabelIds,
	onSelectedLabelIdsChange,
}: DetailsSectionProps): JSX.Element => {
	const {formatMessage} = useIntl();

	// Lifted to the caller (see the selectedLabelIds prop's own comment above).
	const setSelectedLabelIds = onSelectedLabelIdsChange;

	// Remount key for the collapse case below — matches CreateOpportunityModal.tsx's own
	// `dateFieldKey`, which forces the picker to re-read its startDate/endDate props and snap its
	// display back to the retained values, since a discarded change (proposal unchanged) wouldn't
	// otherwise cause the picker to re-sync its own internal segment display.
	const [dateFieldKey, setDateFieldKey] = React.useState(0);

	return (
		<Accordion.Item
			value="details"
			// opportunity.section.details is an existing, currently-unused translation key (sitting
			// alongside opportunity.section.overview/.financial/.more_settings in en.json and
			// defaultMessages.json) — a leftover from when CreateOpportunityModal's own third section
			// was apparently called "Details" before it became "Financial Setup" (matching the Jira
			// ticket's own resolved note: "frames 2/3 label the third accordion 'Details' instead of
			// 'Financial Setup' ... fixed in Figma"). Its English text is exactly "Details", matching
			// this section's own Figma frames (node 240:836, verified directly), and reusing it avoids
			// adding a duplicate key with identical text.
			label={formatMessage({id: 'opportunity.section.details'})}
			content=""
			data-cy="section-details"
		>
			<FlexColumn gap="l">
				<FlexRow gap="l" alignItems="flex-start">
					<FlexItem flex="1 1 0">
						<FlexColumn gap="xs">
							{/* Icon-help next to the label, matching this ticket's own Figma frames (node
								240:1484/240:1771/240:1900, confirmed directly) — and CreateOpportunityModal.tsx's
								own identical composition (:599-604) for the same field, reusing its exact
								tooltip copy (opportunity.dates_tooltip) rather than inventing new text. */}
							<FlexRow gap="xs" alignItems="center">
								<Label>{formatMessage({id: 'opportunity.field.project_dates'})}</Label>
								<IconTooltip>
									<IconTooltip.Item title={formatMessage({id: 'opportunity.dates_tooltip'})} />
								</IconTooltip>
							</FlexRow>
							<DateRangePicker
								key={dateFieldKey}
								data-cy="ai-project-dates"
								aria-label={formatMessage({id: 'opportunity.field.project_dates'})}
								startDate={proposal.start_date ?? undefined}
								endDate={proposal.end_date ?? undefined}
								onChange={({startDate, endDate}: {startDate?: string; endDate?: string}) => {
									// The picker reports the interval atomically, not as a delta —
									// CreateOpportunityModal.tsx's own resolveDateRangeChange documents this
									// exactly. Clearing the START segment collapses the WHOLE interval (both
									// sides come back undefined) even though the user never touched end.
									// Discard that case entirely — applying it would silently wipe a start
									// date the user didn't ask to clear — and remount the field so its
									// display snaps back to the retained values instead of showing a stale
									// blank state.
									if (startDate === undefined) {
										setDateFieldKey(key => key + 1);
										return;
									}
									// An interval that still carries a start but OMITS endDate is what the
									// picker produces when only the end segment is cleared — the same shape
									// it uses whenever end is genuinely unchanged (it echoes back the still-
									// valid start on every change, including ones that only touched end).
									// So "startDate is present" can't be read as "the user edited start_date":
									// on an end-only clear that would wrongly mark start_date edited and
									// permanently lock it against a later Nova-provided value via
									// mergeProposalEdits. Comparing each incoming value against the CURRENT
									// proposal value — not just checking whether the payload carries it — is
									// what tells an actual edit apart from an echoed, unchanged side; an
									// omitted endDate is treated as "cleared to null", matching
									// resolveDateRangeChange's own `intervalToMoments` conversion.
									const nextEndDate = endDate ?? null;
									if (startDate !== proposal.start_date) onFieldUserEdited('start_date');
									if (nextEndDate !== proposal.end_date) onFieldUserEdited('end_date');
									onProposalChange({...proposal, start_date: startDate, end_date: nextEndDate});
								}}
							/>
						</FlexColumn>
					</FlexItem>
					<FlexItem flex="1 1 0">
						<FlexColumn gap="xs">
							{/* common.color renders "Color" (US spelling) — confirmed against both this
								codebase's own convention (CreateOpportunityModal uses the same key for the
								same field) and the actual Figma frames (node 240:836), which render "Color",
								not "Colour". */}
							<Label>{formatMessage({id: 'common.color'})}</Label>
							<FlexRow gap="s" alignItems="center">
								{projectColors.slice(0, 6).map(colorOption => (
									<button
										key={colorOption}
										type="button"
										data-cy={`ai-color-${colorOption}`}
										aria-label={colorOption}
										aria-pressed={proposal.color === colorOption}
										onClick={() => {
											onFieldUserEdited('color');
											onProposalChange({...proposal, color: colorOption});
										}}
										style={{
											width: 32,
											height: 32,
											flex: '0 0 auto',
											borderRadius: 4,
											cursor: 'pointer',
											backgroundColor: colorOption,
											border: proposal.color === colorOption ? '2px solid #6C5CE7' : '1px solid #E6E7EB',
										}}
									/>
								))}
								<FlexItem flex="1 1 0">
									<TextField
										data-cy="ai-color-hex"
										aria-label={formatMessage({id: 'common.color'})}
										value={proposal.color}
										onValueChange={(value: string) => {
											if (/^#([0-9A-Fa-f]{0,6})$/.test(value)) {
												onFieldUserEdited('color');
												onProposalChange({...proposal, color: value});
											}
										}}
									/>
								</FlexItem>
							</FlexRow>
						</FlexColumn>
					</FlexItem>
				</FlexRow>
				{/* Skip-only: the Missing Project Dates callout never carries candidates (no plausible
					guess to offer), matching the spec's confirmed placement — between the dates/colour
					row and Labels. */}
				{datesCallout && (
					<ResolutionCallout question={datesCallout.question} candidates={[]} onResolve={onCalloutResolve} />
				)}
				<FlexRow>
					<FlexItem flex="1 1 100%">
						<FlexColumn gap="xs">
							{/* common.labels renders exactly "Labels", matching the Figma frames — NOT
								project_settings.project-labels, which renders "Project labels" (wrong text
								for this field: the Figma label here has no "Project" prefix). */}
							<Label>{formatMessage({id: 'common.labels'})}</Label>
							<LabelDropdown
								// LabelDropdown's own prop type is generated from a Relay fragment spread
								// (LabelDropdown_labels$data), which carries a compile-time-only "$fragmentType"
								// brand that a plain query field's data never has — CreateOpportunityModal.tsx
								// hits the exact same shape mismatch feeding this same component from its own
								// plain `labels` query field, invisibly, only because its `viewer` prop is typed
								// `any`. Our data is genuinely structurally identical (id/name/color/category,
								// verified against LabelDropdown_labels' own fragment definition); this cast
								// names that at the one boundary where it's needed instead of widening a type
								// to `any`.
								labels={labels as unknown as LabelDropdown_labels$data[0][]}
								selectedItems={selectedLabelIds}
								setSelectedItems={setSelectedLabelIds}
								// The large size, so it matches the 44px height of every other field in this form.
								height="large"
								data-cy="ai-labels"
							/>
						</FlexColumn>
					</FlexItem>
				</FlexRow>
				{/* Duplicate Project: a Toggle below Labels, styled like Use Baseline/Create Invoices if
					Available, with the toggle before its label like every toggle in this form. A
					duplicate and a SoW extraction are mutually exclusive, and the design shows no toggle
					at all once a document is attached, so it is hidden rather than greyed out. It comes
					back if the document is rejected or the read is cancelled. */}
				{!duplicateProjectDisabled && (
					<FlexRow gap="l" alignItems="center">
						<FlexRow gap="s" alignItems="center" flex="0 0 auto">
							<Toggle checked={duplicateProject} onChange={onDuplicateProjectToggle} cy="ai-duplicate-project" />
							<Text>{formatMessage({id: 'ai_project_planning.duplicate_existing_project'})}</Text>
						</FlexRow>
					</FlexRow>
				)}
				{/* TNSR-635 review (Animesh's manual QA): the picker and clone-scope checkboxes below
					must actually disappear once a SoW is attached mid-setup, not just have their toggle
					greyed out while staying on screen — a duplicate a viewer can no longer submit (see
					handleApprove's own `uploadRegionState === 'no-file'` guard) has nothing left to show. */}
				{duplicateProject && !duplicateProjectDisabled && (
					<FlexColumn gap="l">
						<FlexRow>
							<FlexItem flex="1 1 100%">
								<FlexColumn gap="xs">
									<Label>{formatMessage({id: 'opportunity.project_to_duplicate'})}</Label>
									<DropdownList>
										<DropdownList.Trigger
											data-cy="ai-project-to-duplicate"
											aria-label={formatMessage({id: 'opportunity.project_to_duplicate'})}
										>
											<ButtonDropdownField>
												<Text>
													{projectToDuplicate
														? `${projectToDuplicate.customProjectId ?? ''} ${
																projectToDuplicate.name
														  }`.trim()
														: formatMessage({id: 'common.none'})}
												</Text>
											</ButtonDropdownField>
										</DropdownList.Trigger>
										<DropdownList.Content align="start" useTriggerWidth>
											<DropdownList.Item
												data-cy="ai-project-to-duplicate-none"
												onClick={() => onProjectToDuplicateChange(null)}
											>
												<Text>{formatMessage({id: 'common.none'})}</Text>
											</DropdownList.Item>
											{duplicateableProjects.map(project => (
												<DropdownList.Item
													key={project.id}
													data-cy={`ai-project-to-duplicate-${project.id}`}
													onClick={() => onProjectToDuplicateChange(project)}
												>
													<Text>{`${project.customProjectId ?? ''} ${project.name}`.trim()}</Text>
												</DropdownList.Item>
											))}
										</DropdownList.Content>
									</DropdownList>
								</FlexColumn>
							</FlexItem>
						</FlexRow>
						{/* Clone-scope checkboxes — field-for-field the same groups/labels/i18n keys as
							CreateOpportunityModal.tsx's own identical duplicate-picker (:1021-1122), the one
							other place in this codebase that already drives DuplicateProjectMutation from a
							design-system UI. Only rendered once a source project is picked — nothing to scope
							a clone of before then. TNSR-635 review (Animesh's manual QA against Figma): no
							"Duplicate:" heading above the groups — Figma has no such label here (an earlier
							pass added one that doesn't exist in the design); the outer FlexColumn's own `gap="l"`
							already gives Project to Duplicate and Structure/Resourcing/Financials/Setup the same
							spacing every other sibling field in this section uses. */}
						{projectToDuplicate && (
							<FlexColumn gap="s">
								<FlexRow gap="l" alignItems="flex-start">
									<FlexItem flex="1 1 0">
										<FlexColumn gap="xs">
											<Label>{formatMessage({id: 'opportunity.duplicate_group.structure'})}</Label>
											<Checkbox
												data-cy="ai-duplicate-tasks"
												notBoldLabel
												checked={duplicateScope.tasks}
												onChange={checked =>
													onDuplicateScopeChange({...duplicateScope, tasks: !!checked})
												}
												labelText={formatMessage({id: 'opportunity.duplicate_item.tasks'})}
											/>
											<Checkbox
												data-cy="ai-duplicate-phases"
												notBoldLabel
												checked={duplicateScope.phases}
												onChange={checked =>
													onDuplicateScopeChange({...duplicateScope, phases: !!checked})
												}
												labelText={formatMessage({id: 'opportunity.duplicate_item.phases'})}
											/>
											{/* TNSR-635 review (Animesh's manual QA against Figma): Deliverables renders
												unconditionally alongside Tasks/Phases — Figma shows all three regardless of
												the picked project, unlike CreateOpportunityModal.tsx's own gate on
												useDeliverables for a brand-new (not-yet-existing) opportunity's project. */}
											<Checkbox
												data-cy="ai-duplicate-deliverables"
												notBoldLabel
												checked={duplicateScope.deliverables}
												onChange={checked =>
													onDuplicateScopeChange({...duplicateScope, deliverables: !!checked})
												}
												labelText={formatMessage({id: 'common.deliverables'})}
											/>
										</FlexColumn>
									</FlexItem>
									<FlexItem flex="1 1 0">
										<FlexColumn gap="xs">
											<Label>{formatMessage({id: 'opportunity.duplicate_group.resourcing'})}</Label>
											<Checkbox
												data-cy="ai-duplicate-team"
												notBoldLabel
												checked={duplicateScope.team}
												onChange={checked =>
													onDuplicateScopeChange({...duplicateScope, team: !!checked})
												}
												labelText={formatMessage({id: 'opportunity.duplicate_item.team_members'})}
											/>
											{duplicateScope.team && (
												<Checkbox
													data-cy="ai-duplicate-allocations"
													notBoldLabel
													checked={duplicateScope.allocations}
													onChange={checked =>
														onDuplicateScopeChange({...duplicateScope, allocations: !!checked})
													}
													labelText={formatMessage({id: 'opportunity.duplicate_item.allocations'})}
												/>
											)}
											{hasModule(MODULE_TYPES.PLACEHOLDERS) && (
												<Checkbox
													data-cy="ai-duplicate-placeholders"
													notBoldLabel
													checked={duplicateScope.placeholders}
													onChange={checked =>
														onDuplicateScopeChange({...duplicateScope, placeholders: !!checked})
													}
													labelText={formatMessage({id: 'opportunity.duplicate_item.placeholders'})}
												/>
											)}
										</FlexColumn>
									</FlexItem>
									{CompanySetupUtil.hasFinance() && (
										<FlexItem flex="1 1 0">
											<FlexColumn gap="xs">
												<Label>{formatMessage({id: 'opportunity.duplicate_group.financials'})}</Label>
												<Checkbox
													data-cy="ai-duplicate-expenses"
													notBoldLabel
													checked={duplicateScope.expenses}
													onChange={checked =>
														onDuplicateScopeChange({...duplicateScope, expenses: !!checked})
													}
													labelText={formatMessage({id: 'project_budget.expenses'})}
												/>
												<Checkbox
													data-cy="ai-duplicate-retainer-periods"
													notBoldLabel
													checked={duplicateScope.retainerPeriods}
													onChange={checked =>
														onDuplicateScopeChange({...duplicateScope, retainerPeriods: !!checked})
													}
													labelText={formatMessage({
														id: 'opportunity.duplicate_item.retainer_periods',
													})}
												/>
											</FlexColumn>
										</FlexItem>
									)}
									<FlexItem flex="1 1 0">
										<FlexColumn gap="xs">
											<Label>{formatMessage({id: 'opportunity.duplicate_group.setup'})}</Label>
											<Checkbox
												data-cy="ai-duplicate-settings"
												notBoldLabel
												checked={duplicateScope.settings}
												onChange={checked =>
													onDuplicateScopeChange({...duplicateScope, settings: !!checked})
												}
												labelText={formatMessage({id: 'duplicate-modal.settings-v2'})}
											/>
											<Checkbox
												data-cy="ai-duplicate-color"
												notBoldLabel
												checked={duplicateScope.color}
												onChange={checked =>
													onDuplicateScopeChange({...duplicateScope, color: !!checked})
												}
												labelText={formatMessage({id: 'integration.project_templates.project_color'})}
											/>
										</FlexColumn>
									</FlexItem>
								</FlexRow>
							</FlexColumn>
						)}
					</FlexColumn>
				)}
			</FlexColumn>
		</Accordion.Item>
	);
};

export default DetailsSection;
